import "@ui5/webcomponents-icons/dist/search.js";
import "@ui5/webcomponents-icons/dist/slim-arrow-down.js";
import { Icon, Input, Popover, type InputDomRef } from "@ui5/webcomponents-react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from "react";
import { useTranslation } from "react-i18next";

import { compactNavigation, type NavigationEntry } from "./navigation";
import { filterNavigation, firstPath, matchRanges, searchTerms } from "./navigationSearch";
import "./SideNav.css";

export interface SideNavProps {
  /** Navigation already filtered by permissions and plan modules. */
  navigation: readonly NavigationEntry[];
  currentPath: string;
  /** Icon rail (collapsed Navigation Layout): groups open in a popover. */
  collapsed: boolean;
  onNavigate: (path: string) => void;
  /** Labels of an entry in every language (the search matches all of them). */
  labelsOf: (entry: NavigationEntry) => readonly string[];
  searchRef?: Ref<InputDomRef>;
  /** Asks the shell to show the full navigation (search from the rail). */
  onExpandRequest?: () => void;
  /** Set by the Navigation Layout's `sideContent` slot. */
  slot?: string;
}

/** A visible node of the tree with its depth (1 module, 2 screen or level-3 group, 3 screen) and its ancestors. */
interface Row {
  entry: NavigationEntry;
  depth: number;
  parent: Row | undefined;
}

const storageKey = "positive.shell.navigation.expanded";

function readExpanded(): Set<string> {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(storageKey) ?? "[]");
    return new Set(Array.isArray(stored) ? stored.filter((key): key is string => typeof key === "string") : []);
  } catch {
    return new Set();
  }
}

function writeExpanded(keys: ReadonlySet<string>): void {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify([...keys]));
  } catch {
    // Session storage unavailable (private mode, blocked): the expanded groups are simply not remembered.
  }
}

/** All rows of a tree, depth first, regardless of expansion. */
function allRows(entries: readonly NavigationEntry[], depth: number, parent?: Row): Row[] {
  return entries.flatMap((entry) => {
    const row: Row = { entry, depth, parent };
    return [row, ...allRows(entry.children ?? [], depth + 1, row)];
  });
}

function ancestors(row: Row | undefined): Row[] {
  const result: Row[] = [];
  for (let current = row?.parent; current; current = current.parent) result.unshift(current);
  return result;
}

/** The screen of the current route: the exact path, else the longest path the route lies under (record pages). */
function selectedRow(rows: readonly Row[], currentPath: string): Row | undefined {
  let best: Row | undefined;
  for (const row of rows) {
    const path = row.entry.path;
    if (path === undefined || row.entry.children) continue;
    const hit = path === currentPath || (path !== "/" && currentPath.startsWith(`${path}/`));
    if (hit && (best?.entry.path === undefined || path.length > best.entry.path.length)) best = row;
  }
  return best;
}

const isGroup = (entry: NavigationEntry) => Boolean(entry.children?.length);

function Highlight({ text, terms }: { text: string; terms: readonly string[] }) {
  const ranges = matchRanges(text, terms);
  if (ranges.length === 0) return <>{text}</>;
  const parts: ReactNode[] = [];
  let at = 0;
  for (const [start, end] of ranges) {
    if (start > at) parts.push(text.slice(at, start));
    parts.push(
      <mark key={start} className="positive-sn-mark">
        {text.slice(start, end)}
      </mark>,
    );
    at = end;
  }
  if (at < text.length) parts.push(text.slice(at));
  return <>{parts}</>;
}

interface TreeProps {
  entries: readonly NavigationEntry[];
  /** Depth of the first level (1 in the side navigation, 2 in a rail popover). */
  baseDepth: number;
  expanded: ReadonlySet<string>;
  onToggle: (key: string, open: boolean) => void;
  selectedKey: string | undefined;
  terms: readonly string[];
  accessibleName: string;
  labelOf: (entry: NavigationEntry) => string;
  onActivate: (path: string) => void;
  "data-testid": string;
}

/**
 * WAI-ARIA tree of the navigation (UX11): treeitems with aria-level 1-3, aria-expanded on groups, one tab stop (roving
 * tabindex), arrows move (start/end arrows collapse/expand following the page direction), Home/End, Enter/Space open a
 * screen or toggle a group. Screens are links, so Ctrl+click opens them in a new tab.
 */
function NavigationTree({
  entries,
  baseDepth,
  expanded,
  onToggle,
  selectedKey,
  terms,
  accessibleName,
  labelOf,
  onActivate,
  "data-testid": testId,
}: TreeProps) {
  const treeRef = useRef<HTMLUListElement>(null);
  const [focusKey, setFocusKey] = useState<string>();
  const rows = useMemo(() => allRows(entries, baseDepth), [entries, baseDepth]);
  const visible = useMemo(
    () => rows.filter((row) => ancestors(row).every((ancestor) => expanded.has(ancestor.entry.key))),
    [rows, expanded],
  );
  // The tab stop: the focused row while visible, else the current screen or its nearest visible group, else the first.
  const tabStop = useMemo(() => {
    const keys = new Set(visible.map((row) => row.entry.key));
    if (focusKey !== undefined && keys.has(focusKey)) return focusKey;
    const selected = rows.find((row) => row.entry.key === selectedKey);
    const candidates = selected ? [selected, ...ancestors(selected).reverse()] : [];
    return candidates.find((row) => keys.has(row.entry.key))?.entry.key ?? visible[0]?.entry.key;
  }, [visible, rows, focusKey, selectedKey]);

  const focusRow = (key: string) => {
    setFocusKey(key);
    treeRef.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(key)}"]`)?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const key = (event.target as HTMLElement).closest<HTMLElement>("[data-key]")?.dataset["key"];
    const index = visible.findIndex((row) => row.entry.key === key);
    const row = visible[index];
    if (!row) return;
    const rtl = getComputedStyle(event.currentTarget).direction === "rtl";
    const open = isGroup(row.entry) && expanded.has(row.entry.key);
    let handled = true;
    switch (event.key) {
      case "ArrowDown":
        if (visible[index + 1]) focusRow(visible[index + 1]?.entry.key ?? row.entry.key);
        break;
      case "ArrowUp":
        if (index > 0) focusRow(visible[index - 1]?.entry.key ?? row.entry.key);
        break;
      case "Home":
        focusRow(visible[0]?.entry.key ?? row.entry.key);
        break;
      case "End":
        focusRow(visible.at(-1)?.entry.key ?? row.entry.key);
        break;
      case rtl ? "ArrowLeft" : "ArrowRight":
        if (isGroup(row.entry) && !open) onToggle(row.entry.key, true);
        else if (open) focusRow(row.entry.children?.[0]?.key ?? row.entry.key);
        break;
      case rtl ? "ArrowRight" : "ArrowLeft":
        if (open) onToggle(row.entry.key, false);
        else if (row.parent && row.parent.depth >= baseDepth) focusRow(row.parent.entry.key);
        break;
      case "Enter":
      case " ":
        if (isGroup(row.entry)) onToggle(row.entry.key, !open);
        else if (row.entry.path) onActivate(row.entry.path);
        break;
      default:
        handled = false;
    }
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const renderLevel = (levelEntries: readonly NavigationEntry[], depth: number, trail: readonly string[]) =>
    levelEntries.map((entry) => {
      const label = labelOf(entry);
      const group = isGroup(entry);
      const open = group && expanded.has(entry.key);
      const selected = entry.key === selectedKey;
      const level = depth - baseDepth + 1;
      // A level-3 group whose screen is open is marked, so the path to the screen stays visible when it is closed.
      const containsSelected =
        group && selectedKey !== undefined && allRows(entry.children ?? [], 0).some((r) => r.entry.key === selectedKey);
      const className = [
        "positive-sn-item",
        `positive-sn-depth-${String(depth)}`,
        group ? "positive-sn-group" : "positive-sn-screen",
        entry.subGroup && group ? "positive-sn-subgroup" : "",
        open ? "positive-sn-open" : "",
        selected ? "positive-sn-selected" : "",
        containsSelected ? "positive-sn-has-selected" : "",
      ]
        .filter(Boolean)
        .join(" ");
      const showTrail = terms.length > 0 && depth === 3 && trail.length > 0;
      const content = (
        <>
          {depth === 3 && !group ? (
            <span className="positive-sn-dot" aria-hidden="true" />
          ) : (
            <Icon name={entry.icon} className="positive-sn-icon" />
          )}
          <span className="positive-sn-text">
            <span className="positive-sn-label">
              <Highlight text={label} terms={terms} />
            </span>
            {showTrail && (
              <span className="positive-sn-trail" data-testid="side-nav-trail">
                {trail.map((part) => (
                  <span key={part} className="positive-sn-trail-part">
                    {part}
                  </span>
                ))}
              </span>
            )}
          </span>
          {group && <Icon name="slim-arrow-down" className="positive-sn-chevron" />}
        </>
      );
      const common = {
        role: "treeitem",
        "aria-level": level,
        "data-key": entry.key,
        "data-testid": `side-nav-item-${entry.key}`,
        tabIndex: entry.key === tabStop ? 0 : -1,
        className,
        title: [...trail, label].join(" › "),
        onFocus: () => {
          setFocusKey(entry.key);
        },
      } as const;
      return (
        <li key={entry.key} role="none" className="positive-sn-node">
          {group ? (
            <div
              {...common}
              aria-expanded={open}
              onClick={() => {
                setFocusKey(entry.key);
                onToggle(entry.key, !open);
              }}
            >
              {content}
            </div>
          ) : (
            <a
              {...common}
              href={entry.path}
              data-path={entry.path}
              aria-selected={selected}
              aria-current={selected ? "page" : undefined}
              onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
                event.preventDefault();
                setFocusKey(entry.key);
                if (entry.path) onActivate(entry.path);
              }}
            >
              {content}
            </a>
          )}
          {group && (
            <div className={open ? "positive-sn-collapse positive-sn-collapse-open" : "positive-sn-collapse"}>
              <div className="positive-sn-clip">
                <ul role="group" className={`positive-sn-panel positive-sn-panel-${String(depth + 1)}`}>
                  {renderLevel(entry.children ?? [], depth + 1, [...trail, label])}
                </ul>
              </div>
            </div>
          )}
        </li>
      );
    });

  return (
    <ul
      ref={treeRef}
      role="tree"
      aria-label={accessibleName}
      className={`positive-sn-tree positive-sn-tree-${String(baseDepth)}`}
      data-testid={testId}
      onKeyDown={onKeyDown}
    >
      {renderLevel(entries, baseDepth, [])}
    </ul>
  );
}

/**
 * Side navigation of the shell (ADR-0010/D-2, UX5, UX11): menu search, then the modules (level 1) with their screens
 * (level 2) and the level-3 groups of long modules, each level with its own look. Collapsed, it is an icon rail whose
 * modules open in a popover with their screens and level-3 groups.
 */
export function SideNav({
  navigation,
  currentPath,
  collapsed,
  onNavigate,
  labelsOf,
  searchRef,
  onExpandRequest,
  slot,
}: SideNavProps) {
  const { t } = useTranslation("shell");
  const [query, setQuery] = useState("");
  const [stored, setStored] = useState(readExpanded);
  const compact = useMemo(() => compactNavigation(navigation), [navigation]);
  const filtered = useMemo(() => filterNavigation(compact, query, labelsOf), [compact, query, labelsOf]);
  const terms = useMemo(() => searchTerms(query), [query]);
  const searching = terms.length > 0;
  const rows = useMemo(() => allRows(compact, 1), [compact]);
  const selected = useMemo(() => selectedRow(rows, currentPath), [rows, currentPath]);
  const labelOf = useCallback((entry: NavigationEntry) => t(entry.labelKey), [t]);

  // Opening a screen (or arriving by URL) expands the groups above it, once per route: the user may close them again.
  const [expandedFor, setExpandedFor] = useState<string>();
  if (expandedFor !== currentPath) {
    setExpandedFor(currentPath);
    const missing = ancestors(selected).filter((row) => !stored.has(row.entry.key));
    if (missing.length > 0) {
      const next = new Set(stored);
      for (const row of missing) next.add(row.entry.key);
      writeExpanded(next);
      setStored(next);
    }
  }

  // While searching every group of the result is open (both levels), without touching the remembered state.
  const expanded = useMemo(
    () =>
      searching ? new Set(allRows(filtered, 1).flatMap((row) => (isGroup(row.entry) ? [row.entry.key] : []))) : stored,
    [searching, filtered, stored],
  );
  const toggle = useCallback(
    (key: string, open: boolean) => {
      if (searching) return;
      setStored((current) => {
        const next = new Set(current);
        if (open) next.add(key);
        else next.delete(key);
        writeExpanded(next);
        return next;
      });
    },
    [searching],
  );

  const activate = useCallback(
    (path: string) => {
      setQuery("");
      onNavigate(path);
    },
    [onNavigate],
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const onSearchKeyDown = (event: KeyboardEvent<InputDomRef>) => {
    if (event.key === "Escape") {
      setQuery("");
    } else if (event.key === "Enter") {
      const path = firstPath(filtered);
      if (path) activate(path);
    } else if (event.key === "ArrowDown") {
      // Into the result list: the first entry takes the focus.
      const first = scrollRef.current?.querySelector<HTMLElement>('[role="treeitem"]');
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  // RV-S-11: the current screen is scrolled into view of the menu (after its groups opened), not left below the fold.
  const selectedKey = selected?.entry.key;
  useEffect(() => {
    if (collapsed || selectedKey === undefined) return;
    const timer = window.setTimeout(() => {
      const row = scrollRef.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(selectedKey)}"]`);
      // jsdom (unit tests) has no scrollIntoView.
      if (row && typeof (row as Partial<HTMLElement>).scrollIntoView === "function")
        row.scrollIntoView({ block: "nearest" });
    }, 260);
    return () => {
      window.clearTimeout(timer);
    };
  }, [collapsed, currentPath, selectedKey]);

  const navClass = collapsed ? "positive-sn positive-sn-rail" : "positive-sn";
  return (
    <nav slot={slot} className={navClass} aria-label={t("mainNavigation")} data-testid="side-nav">
      {collapsed ? (
        <Rail
          entries={compact}
          selected={selected}
          labelOf={labelOf}
          onNavigate={onNavigate}
          {...(onExpandRequest ? { onSearch: onExpandRequest } : {})}
        />
      ) : (
        <>
          <div className="positive-sn-search">
            <Input
              ref={searchRef}
              type="Search"
              value={query}
              showClearIcon
              placeholder={t("menuSearch.placeholder")}
              accessibleName={t("menuSearch.label")}
              icon={<Icon name="search" />}
              data-testid="menu-search"
              onInput={(event) => {
                setQuery(event.target.value);
              }}
              onKeyDown={onSearchKeyDown}
            />
            {searching && filtered.length === 0 && (
              <p className="positive-sn-search-empty" role="status" data-testid="menu-search-empty">
                {t("menuSearch.noResults")}
              </p>
            )}
          </div>
          <div className="positive-sn-scroll" ref={scrollRef}>
            <NavigationTree
              entries={filtered}
              baseDepth={1}
              expanded={expanded}
              onToggle={toggle}
              selectedKey={selected?.entry.key}
              terms={terms}
              accessibleName={t("mainNavigation")}
              labelOf={labelOf}
              onActivate={activate}
              data-testid="side-nav-tree"
            />
          </div>
        </>
      )}
    </nav>
  );
}

interface RailProps {
  entries: readonly NavigationEntry[];
  selected: Row | undefined;
  labelOf: (entry: NavigationEntry) => string;
  onNavigate: (path: string) => void;
  onSearch?: () => void;
}

/** Collapsed navigation: one icon per module; a module opens a popover with its screens and level-3 groups. */
function Rail({ entries, selected, labelOf, onNavigate, onSearch }: RailProps) {
  const { t } = useTranslation("shell");
  const listRef = useRef<HTMLUListElement>(null);
  const [openKey, setOpenKey] = useState<string>();
  const [opener, setOpener] = useState<HTMLElement | null>(null);
  const [popoverExpanded, setPopoverExpanded] = useState<ReadonlySet<string>>(new Set());
  const [focusIndex, setFocusIndex] = useState(0);
  const selectedModule = ancestors(selected)[0]?.entry.key ?? selected?.entry.key;
  const openEntry = entries.find((entry) => entry.key === openKey);

  const openGroup = (entry: NavigationEntry, element: HTMLElement) => {
    // Level-3 groups start open in the popover: everything of the module is one look away.
    setPopoverExpanded(new Set((entry.children ?? []).filter(isGroup).map((child) => child.key)));
    setOpener(element);
    setOpenKey(entry.key);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const buttons = [...(listRef.current?.querySelectorAll<HTMLElement>(".positive-sn-rail-item") ?? [])];
    const index = buttons.indexOf(event.target as HTMLElement);
    if (index === -1) return;
    const next =
      event.key === "ArrowDown"
        ? Math.min(index + 1, buttons.length - 1)
        : event.key === "ArrowUp"
          ? Math.max(index - 1, 0)
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? buttons.length - 1
              : undefined;
    if (next === undefined) return;
    event.preventDefault();
    setFocusIndex(next);
    buttons[next]?.focus();
  };

  const items = entries.map((entry, position) => {
    const index = position + (onSearch ? 1 : 0);
    const label = labelOf(entry);
    const current = entry.key === selectedModule;
    const className = [
      "positive-sn-rail-item",
      current ? "positive-sn-selected" : "",
      entry.key === openKey ? "positive-sn-open" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <li key={entry.key}>
        {isGroup(entry) ? (
          <button
            type="button"
            className={className}
            aria-label={label}
            title={label}
            aria-haspopup="dialog"
            aria-expanded={entry.key === openKey}
            tabIndex={focusIndex === index ? 0 : -1}
            data-key={entry.key}
            data-testid={`side-nav-rail-${entry.key}`}
            onClick={(event) => {
              setFocusIndex(index);
              openGroup(entry, event.currentTarget);
            }}
          >
            <Icon name={entry.icon} className="positive-sn-icon" />
          </button>
        ) : (
          <a
            href={entry.path}
            className={className}
            aria-label={label}
            title={label}
            aria-current={current ? "page" : undefined}
            tabIndex={focusIndex === index ? 0 : -1}
            data-path={entry.path}
            data-testid={`side-nav-rail-${entry.key}`}
            onClick={(event) => {
              if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              setFocusIndex(index);
              if (entry.path) onNavigate(entry.path);
            }}
          >
            <Icon name={entry.icon} className="positive-sn-icon" />
          </a>
        )}
      </li>
    );
  });

  return (
    <>
      <ul ref={listRef} className="positive-sn-rail-list" onKeyDown={onKeyDown} data-testid="side-nav-rail">
        {onSearch && (
          <li>
            <button
              type="button"
              className="positive-sn-rail-item"
              aria-label={t("menuSearch.label")}
              title={t("menuSearch.label")}
              tabIndex={focusIndex === 0 ? 0 : -1}
              data-testid="side-nav-rail-search"
              onClick={onSearch}
            >
              <Icon name="search" className="positive-sn-icon" />
            </button>
          </li>
        )}
        {items}
      </ul>
      <Popover
        open={openEntry !== undefined}
        opener={opener ?? undefined}
        placement="End"
        className="positive-sn-popover"
        headerText={openEntry ? labelOf(openEntry) : ""}
        data-testid="side-nav-popover"
        onClose={() => {
          setOpenKey(undefined);
        }}
      >
        {openEntry && (
          <Fragment key={openEntry.key}>
            <NavigationTree
              entries={openEntry.children ?? []}
              baseDepth={2}
              expanded={popoverExpanded}
              onToggle={(key, open) => {
                setPopoverExpanded((current) => {
                  const next = new Set(current);
                  if (open) next.add(key);
                  else next.delete(key);
                  return next;
                });
              }}
              selectedKey={selected?.entry.key}
              terms={[]}
              accessibleName={labelOf(openEntry)}
              labelOf={labelOf}
              onActivate={(path) => {
                setOpenKey(undefined);
                onNavigate(path);
              }}
              data-testid="side-nav-popover-tree"
            />
          </Fragment>
        )}
      </Popover>
    </>
  );
}
